import { useState } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import TextEdit from '../../../molecules/TextEdit';
import DateField from '../../../molecules/DateField';
import CurrencyInput from '../../../molecules/CurrencyInput';
import SelectTextField from '../../../molecules/SelectTextField';
import BeneficiarySelectField from './BeneficiarySelectField';
import { getErrorMessage } from '../../../../utils/formErrorUtils';
import type { Beneficiary, CreateExpensePayload, SelectOption } from '../../../../types/LegalCase';
import type { ErrorField } from '../../../../types/ErrorField';

interface ExpenseFormModalProps {
    categoryOptions: SelectOption[];
    beneficiaries: Beneficiary[];
    beneficiaryTypeOptions: SelectOption[];
    onCreateBeneficiary: (
        data: { name: string; beneficiaryType?: string },
    ) => Promise<Beneficiary | null>;
    beneficiaryErrorFields: ErrorField[];
    isBeneficiarySaving: boolean;
    errorFields: ErrorField[];
    isSaving: boolean;
    onSubmit: (data: CreateExpensePayload) => Promise<boolean>;
    onCancel: () => void;
}

const INLINE_FIELDS = new Set(['category', 'amount', 'paidAt', 'beneficiaryId', 'description']);

const moneyOrUndefined = (value: number): number | undefined =>
    value && value > 0 ? value : undefined;

// Modal de cadastro de despesa na página top-level de Despesas. Categoria e valor são o
// mínimo; beneficiário (com "+ Novo" inline) e data de pagamento são opcionais. Sem vínculo
// de caso aqui — a despesa top-level é a despesa geral do jurídico (sem processo); para
// amarrar a um caso, use a seção Custos do detalhe do caso.
const ExpenseFormModal = ({
    categoryOptions,
    beneficiaries,
    beneficiaryTypeOptions,
    onCreateBeneficiary,
    beneficiaryErrorFields,
    isBeneficiarySaving,
    errorFields,
    isSaving,
    onSubmit,
    onCancel,
}: ExpenseFormModalProps) => {
    const [category, setCategory] = useState(categoryOptions[0]?.value || 'OTHER');
    const [amount, setAmount] = useState(0);
    const [paidAt, setPaidAt] = useState('');
    const [beneficiaryId, setBeneficiaryId] = useState('');
    const [description, setDescription] = useState('');

    const submit = async () => {
        await onSubmit({
            category,
            amount: moneyOrUndefined(amount),
            paidAt: paidAt.trim() || undefined,
            beneficiaryId: beneficiaryId.trim() || undefined,
            description: description.trim() || undefined,
        });
    };

    const residual = errorFields.filter((e) => !INLINE_FIELDS.has(e.fieldName));

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onCancel} />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="w-full max-w-md rounded-lg bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-lg p-4 space-y-3 max-h-[90vh] overflow-auto">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">Nova despesa</h3>

                    <SelectTextField
                        label="Categoria"
                        items={categoryOptions}
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        emptyLabel="Selecione a categoria..."
                        errorMessage={getErrorMessage(errorFields, 'category')}
                    />
                    <CurrencyInput
                        label="Valor"
                        prefix="R$"
                        fractionDigits={2}
                        value={amount}
                        onChange={setAmount}
                        errorMessage={getErrorMessage(errorFields, 'amount')}
                    />
                    <DateField
                        label="Data de pagamento (opcional)"
                        value={paidAt}
                        onChange={setPaidAt}
                        errorMessage={getErrorMessage(errorFields, 'paidAt')}
                    />
                    <BeneficiarySelectField
                        value={beneficiaryId}
                        onChange={setBeneficiaryId}
                        beneficiaries={beneficiaries}
                        beneficiaryTypeOptions={beneficiaryTypeOptions}
                        onCreate={onCreateBeneficiary}
                        isSaving={isBeneficiarySaving}
                        errorFields={beneficiaryErrorFields}
                    />
                    <TextEdit
                        label="Descrição (opcional)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'description')}
                    />

                    {residual.map((e) => (
                        <div key={e.fieldName} className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                            <FiAlertTriangle size={14} />
                            <span>{e.message}</span>
                        </div>
                    ))}

                    <div className="flex items-center justify-end gap-2">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={submit}
                            disabled={isSaving}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                        >
                            {isSaving ? 'Salvando...' : 'Registrar despesa'}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ExpenseFormModal;
